import axios from 'axios';
import { v2Api, API_ROOT } from './v2Client';
import { ensureAccessToken } from './authTokens';
import { clubRequestHeaders } from '../currentClub';

function unwrapArray(data) {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') {
    const o = data;
    const inner = o.predictions ?? o.Predictions ?? o.items ?? o.Items ?? o.rows ?? o.Rows ?? o.data ?? o.Data;
    if (Array.isArray(inner)) return inner;
  }
  return [];
}

export async function fetchMatchOfTheWeek() {
  const { data } = await v2Api.get('/Match/get_match_of_the_week');
  if (!data || typeof data !== 'object') return null;
  // La réponse enveloppe le match dans `match`/`Match` (qui peut valoir `null` explicitement quand
  // aucun match n'est actif) — ne retomber sur `data` lui-même que si cette clé est absente, sinon
  // `match: null` finirait traité comme un match "vide" au lieu de "aucun".
  const m = 'match' in data || 'Match' in data ? data.match ?? data.Match ?? null : data;
  return m && typeof m === 'object' ? m : null;
}

/** Réservé aux admins (Role == 1). enabled=true active CE match (désactive l'ancien actif). */
export async function setMatchOfTheWeek(matchId, adminPlayerId, enabled) {
  const { data } = await v2Api.post('/Match/set_match_of_the_week', {
    match_id: String(matchId ?? ''),
    player_id: String(adminPlayerId ?? ''),
    enabled: Boolean(enabled),
  });
  return data;
}

/** Bascule le badge historique « déjà match de la semaine », indépendamment du match actif.
 * L'API refuse value=false tant que ce match est l'actif (le désactiver d'abord). */
export async function setMotwHistoryBadge(matchId, adminPlayerId, value) {
  const { data } = await v2Api.post('/Match/set_motw_history_badge', {
    match_id: String(matchId ?? ''),
    player_id: String(adminPlayerId ?? ''),
    value: Boolean(value),
  });
  return data;
}

async function uploadMotwPromoImage(matchId, adminPlayerId, file, variant) {
  const fd = new FormData();
  fd.append('match_id', String(matchId ?? ''));
  fd.append('player_id', String(adminPlayerId ?? ''));
  fd.append('file', file);
  const access = await ensureAccessToken(API_ROOT);
  const headers = clubRequestHeaders();
  if (access) headers.Authorization = `Bearer ${access}`;
  const { data } = await axios.post(`${API_ROOT}/Match/upload_motw_promo_${variant}`, fd, {
    headers,
    maxContentLength: 12 * 1024 * 1024,
    maxBodyLength: 12 * 1024 * 1024,
  });
  return data;
}

/** Image promo « bannière » propre à CE match (multipart). Retourne le MatchDto complet. */
export async function uploadMotwPromoBannerImage(matchId, adminPlayerId, file) {
  return uploadMotwPromoImage(matchId, adminPlayerId, file, 'banner');
}

/** Image promo « paysage » propre à CE match (multipart). Retourne le MatchDto complet. */
export async function uploadMotwPromoPaysageImage(matchId, adminPlayerId, file) {
  return uploadMotwPromoImage(matchId, adminPlayerId, file, 'paysage');
}

/** variant: 'banner' ou 'paysage'. */
export async function clearMotwPromoImage(matchId, adminPlayerId, variant) {
  const { data } = await v2Api.post('/Match/clear_motw_promo', {
    match_id: String(matchId ?? ''),
    player_id: String(adminPlayerId ?? ''),
    variant,
  });
  return data;
}

/** Liste brute des pronostics d'un match (admin uniquement, Role == 1 côté API). */
export async function fetchMotwPredictions(matchId, adminPlayerId) {
  const mid = String(matchId ?? '').trim();
  const pid = String(adminPlayerId ?? '').trim();
  if (!mid || !pid) return { supported: true, predictions: [] };

  try {
    const { data } = await v2Api.get('/Match/get_motw_predictions', {
      params: { match_id: mid, player_id: pid },
    });
    return { supported: true, predictions: unwrapArray(data), raw: data };
  } catch (e) {
    return {
      supported: false,
      predictions: [],
      error: e?.response?.data?.message || e?.message || 'API non disponible',
    };
  }
}

/** Tendance des pronostics (pct_a/pct_b/...) pour n'importe quel match, actif ou passé. */
export async function fetchMotwPredictionTrend(matchId) {
  const mid = String(matchId ?? '').trim();
  if (!mid) return null;
  const { data } = await v2Api.get('/Match/get_motw_prediction_trend', { params: { match_id: mid } });
  return data?.prediction_trend ?? null;
}

/** Tous les matchs déjà mis en avant comme match de la semaine (badge permanent), plus récent d'abord. */
export async function fetchMotwHistory() {
  const { data } = await v2Api.get('/Match/get_motw_history');
  return unwrapArray(data?.matches ?? data);
}

